import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTimes } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

interface PriceOption {
  level: string;
  amount: number;
}

interface OutlineStep {
  stepOrder: number;
  title: string;
  description: string;
}

const CreateCourse: React.FC = () => {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [regulations, setRegulations] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [price, setPrice] = useState(0);
  const [installmentCount, setInstallmentCount] = useState<number | undefined>(
    undefined
  );
  const [paymentInfo, setPaymentInfo] = useState("");
  const [priceOptions, setPriceOptions] = useState<PriceOption[]>([]);
  const [outlineSteps, setOutlineSteps] = useState<OutlineStep[]>([]);
  const [startDate, setStartDate] = useState("");
  const [registrationDeadline, setRegistrationDeadline] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      const dto = {
        title,
        description,
        regulations,
        imageUrl,
        price,
        installmentCount,
        paymentInfo,
        priceOptions,
        outlineSteps,
        startDate: startDate || undefined,
        registrationDeadline: registrationDeadline || undefined,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/courses`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dto),
        }
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Error ${res.status}`);
      }
      nav("/admin/manage-courses");
    } catch (e: any) {
      setError(e.message);
    }
  };

  const removePriceOption = (index: number) => {
    setPriceOptions((po) => po.filter((_, i) => i !== index));
  };

  const removeOutlineStep = (index: number) => {
    setOutlineSteps((os) => os.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaPlus />}
        title="Create New Course"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Courses", to: "/admin/manage-courses" },
          { label: "Create Course" },
        ]}
      />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white shadow-lg rounded-xl p-6 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Title <span className="text-red-500">*</span>
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. German Language A1 Course"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                style={{ fontSize: "16px" }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Short Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the course briefly..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                style={{ fontSize: "16px" }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Regulations
              </label>
              <textarea
                value={regulations}
                onChange={(e) => setRegulations(e.target.value)}
                placeholder="List course rules and requirements..."
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                style={{ fontSize: "16px" }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image URL
              </label>
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                style={{ fontSize: "16px" }}
              />
            </div>
          </div>

          {/* Pricing Section */}
          <div className="mb-8 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Pricing Information
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  One-time Price (BDT)
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(+e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Installment Count (Optional)
                </label>
                <input
                  type="number"
                  value={installmentCount || ""}
                  onChange={(e) =>
                    setInstallmentCount(
                      e.target.value ? +e.target.value : undefined
                    )
                  }
                  placeholder="e.g. 3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Info
                </label>
                <input
                  value={paymentInfo}
                  onChange={(e) => setPaymentInfo(e.target.value)}
                  placeholder="e.g. 6-month installments available"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Options (German Levels)
                </label>
                <div className="space-y-2">
                  {priceOptions.map((opt, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        value={opt.level}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPriceOptions((po) =>
                            po.map((p, j) => (j === i ? { ...p, level: v } : p))
                          );
                        }}
                        placeholder="Level (A1, A2, B1...)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        style={{ fontSize: "16px" }}
                      />
                      <input
                        type="number"
                        value={opt.amount}
                        onChange={(e) => {
                          const v = +e.target.value;
                          setPriceOptions((po) =>
                            po.map((p, j) =>
                              j === i ? { ...p, amount: v } : p
                            )
                          );
                        }}
                        placeholder="Amount (BDT)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        style={{ fontSize: "16px" }}
                      />
                      <button
                        onClick={() => removePriceOption(i)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() =>
                    setPriceOptions((po) => [...po, { level: "", amount: 0 }])
                  }
                  className="mt-2 text-[#009fe3] hover:text-[#007cb5] font-medium text-sm flex items-center gap-1"
                >
                  <FaPlus className="text-xs" />
                  Add Price Option
                </button>
              </div>
            </div>
          </div>

          {/* Outline Section */}
          <div className="mb-8 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Course Outline Steps
            </h2>
            <div className="space-y-3">
              {outlineSteps.map((s, i) => (
                <div
                  key={i}
                  className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg"
                >
                  <input
                    type="number"
                    value={s.stepOrder}
                    onChange={(e) => {
                      const v = +e.target.value;
                      setOutlineSteps((os) =>
                        os.map((o, j) => (j === i ? { ...o, stepOrder: v } : o))
                      );
                    }}
                    className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                    placeholder="Order"
                  />
                  <input
                    value={s.title}
                    onChange={(e) =>
                      setOutlineSteps((os) =>
                        os.map((o, j) =>
                          j === i ? { ...o, title: e.target.value } : o
                        )
                      )
                    }
                    placeholder="Step Title"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                  />
                  <input
                    value={s.description}
                    onChange={(e) =>
                      setOutlineSteps((os) =>
                        os.map((o, j) =>
                          j === i ? { ...o, description: e.target.value } : o
                        )
                      )
                    }
                    placeholder="Description"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                  />
                  <button
                    onClick={() => removeOutlineStep(i)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() =>
                setOutlineSteps((os) => [
                  ...os,
                  { stepOrder: os.length + 1, title: "", description: "" },
                ])
              }
              className="mt-3 text-[#009fe3] hover:text-[#007cb5] font-medium text-sm flex items-center gap-1"
            >
              <FaPlus className="text-xs" />
              Add Outline Step
            </button>
          </div>

          {/* Dates Section */}
          <div className="mb-8 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Course Dates (Optional)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registration Deadline
                </label>
                <input
                  type="date"
                  value={registrationDeadline}
                  onChange={(e) => setRegistrationDeadline(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            className="w-full bg-[#009fe3] text-white py-3 rounded-lg hover:bg-[#007cb5] transition-colors font-semibold shadow-md hover:shadow-lg"
          >
            Create Course
          </button>
        </div>
      </main>
    </div>
  );
};

export default CreateCourse;
